// Chama o proxy server-side compartilhado (mesmo do Enkartes/Crieoferta) que
// encapsula a Gemini. Conforme GEMINI_API_KEY.md, a chave fica APENAS no
// servidor `novocrieoferta` (/var/www/apigemini/.env) e o front conhece só
// a URL pública do proxy — que NÃO é segredo.
//
//   POST https://crieoferta.com/apigemini/index.php
//   body: { texto }
//   resposta:  { ok:true, texto:"..." }  |  { ok:false, erro:"..." }
//
// O proxy aplica: whitelist de Origin, whitelist de modelos (bloqueia
// "Nano Banana"), rate-limit por IP, limite de input, IP/API restritos
// na chave Google. Ver doc completo em GEMINI_API_KEY.md.
//
// ⚠️ NUNCA exponha a chave da IA aqui — sempre via proxy.

const ENDPOINT =
  process.env.REACT_APP_GEMINI_PROXY_URL ||
  'https://crieoferta.com/apigemini/index.php';
const MAX_CHARS = 8000;
const TIMEOUT_MS = 25000;

// Pré-normaliza o texto ANTES de mandar para a IA para preservar
// informação que ela costuma descartar (gramagem, "A CADA Xg/Xkg/Xml").
// A IA respeita o que está entre parênteses; usamos isso como cofre.
// Regras:
//   "cada Xg" / "a cada Xg"  -> "(A CADA XG)"  (preserva "a cada")
//   "Xgramas" isolado        -> "Xg"           (compacta para a IA reconhecer)
//   ...e depois, se a quantidade ficou solta no meio da descrição, embrulha
//   ela em "(Xg)" para a IA não descartar (será promovida pelo parser local).
function preNormalizarParaIA(texto) {
  if (!texto) return texto;
  return texto
    .split(/\r?\n/)
    .map((linha) => {
      let l = linha;
      // 1) compacta unidades por extenso
      l = l.replace(/(\d+(?:[.,]\d+)?)\s*gramas?\b/gi, '$1g');
      l = l.replace(/(\d+(?:[.,]\d+)?)\s*quilos?\b/gi, '$1kg');
      l = l.replace(/(\d+(?:[.,]\d+)?)\s*litros?\b/gi, '$1L');
      l = l.replace(/(\d+(?:[.,]\d+)?)\s*mililitros?\b/gi, '$1ml');
      // 2) "cada Xg" / "a cada Xg" -> "(A CADA XG)"
      l = l.replace(
        /\b(?:a\s+)?cada\s+(\d+(?:[.,]\d+)?)\s*(kg|g|ml|l|lt|lts)\b/gi,
        (_m, n, u) => `(A CADA ${n}${u.toUpperCase()})`
      );
      return l;
    })
    .join('\n');
}

export async function organizarListaComIA(texto) {
  if (!texto || !texto.trim()) {
    throw new Error('Texto vazio');
  }
  const preparado = preNormalizarParaIA(texto);
  const recortado = preparado.slice(0, MAX_CHARS);

  // Timeout via AbortController para não pendurar a UI.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  let res;
  try {
    res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ texto: recortado }),
      signal: controller.signal,
    });
  } catch (e) {
    clearTimeout(timer);
    if (e?.name === 'AbortError') throw new Error('Tempo esgotado ao consultar a IA.');
    throw new Error('Sem conexão com o servidor de IA.');
  }
  clearTimeout(timer);

  const ct = res.headers.get('content-type') || '';
  const bruto = await res.text();

  let data = null;
  if (ct.includes('application/json')) {
    try { data = JSON.parse(bruto); } catch { /* ignore */ }
  }

  if (!res.ok) {
    const erroDetalhe = data?.erro || `HTTP ${res.status}`;
    throw new Error(`Proxy IA respondeu ${res.status}: ${erroDetalhe}`);
  }

  if (!data) {
    const preview = bruto.slice(0, 60).replace(/\s+/g, ' ');
    throw new Error(`Proxy IA devolveu resposta inválida ("${preview}…").`);
  }

  if (!data.ok || !data.texto || !String(data.texto).trim()) {
    throw new Error(data?.erro || 'Resposta vazia da IA.');
  }
  return String(data.texto).trim();
}
